import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import userIcon from "../../assets/user.svg";
import logoutIcon from "../../assets/logout.svg";
import "../../styles/components/utils/UserWidgetView.css";


interface UserWidgetProps {
  name?: string;
  email?: string;
  imageUrl?: string;
  onLogout: () => void;
}

const UserWidgetPopoverView = React.forwardRef<HTMLDivElement, UserWidgetProps>(({ name, email, imageUrl, onLogout }, ref) => {
  const { t } = useTranslation();
  const hasTopSection = imageUrl || name || email;
  
  return (
    <div 
      className="user-widget-popover-view-container"
      role="menu"
      aria-label={t("user.userMenu")}
      ref={ref}
    >
      {hasTopSection && (
        <div className="user-info-section">
          {imageUrl && (
            <img src={imageUrl} alt={t("user.userAlt")} className="user-avatar" />
          )}
          <div className="user-details">
            {name && <div className="user-name">{name}</div>}
            {email && <div className="user-email">{email}</div>}
          </div>
        </div>
      )}
      <button className="logout-button" onClick={onLogout}>
        <img src={logoutIcon} alt={t("user.logoutAlt")} className="logout-icon" />
        <span>{t("user.logOut")}</span>
      </button>
    </div>
  );
});

type UserWidgetButtonProps = {
  imageUrl?: string;
  isOpen: boolean;
  onToggle: () => void;
};

const UserWidgetButtonView = React.forwardRef<HTMLButtonElement, UserWidgetButtonProps>(({ imageUrl, isOpen, onToggle }, ref) => {
  const { t } = useTranslation();
  
  return (
    <button 
      className="user-widget-button-view-container" 
      type="button"
      aria-expanded={isOpen}
      ref={ref}
      onClick={onToggle}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={t("user.userAlt")} className="user-image" />
      ) : (
        <img src={userIcon} alt={t("user.userAlt")} className="user-icon" />
      )}
    </button>
  );
});

const UserWidget: React.FC<UserWidgetProps> = ({ name, email, imageUrl, onLogout }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const toggle = () => setOpen((o) => !o);
  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
        btnRef.current?.focus();
      }
    };
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target)) return;
      if (btnRef.current?.contains(target)) return;
      close();
    };

    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickOutside);

    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [open]);

  return (
    <div>
      <UserWidgetButtonView
        ref={btnRef}
        imageUrl={imageUrl}
        isOpen={open}
        onToggle={toggle}
      />
      {open &&
        createPortal(
          <UserWidgetPopoverView
            ref={popRef}
            name={name}
            email={email}
            imageUrl={imageUrl}
            onLogout={onLogout}
          />,
          document.body
        )
      }
    </div>
  );
};

const UserWidgetOnly: React.FC = () => {
  return (
    <UserWidget
      name={"Luke Skywalker"}
      email={"luke@skywalker.com"}
      imageUrl={"https://placecats.com/bella/50/50"}
      onLogout={() => {}}
    />
  )
}

export default UserWidget;
export { UserWidgetOnly };
